// src/components/pos/CurrentOrder.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Minus, Trash2, User, Loader2, AlertCircle, X, Gift } from 'lucide-react';
import ApiService from '../../services/api';
import CustomerSelectionModal from '../modals/CustomerSelectionModalFixed';
import CustomerService from '../../services/customerService';
import systemConfigService from '../../services/systemConfigService';
import { DEFAULT_CUSTOMERS } from '../../config/constants';
import WebPromotionsPopup from './WebPromotionsPopup';
import webPromotionService from '../../services/webPromotionService';

const CurrentOrder = ({ 
  cartItems = [], 
  setCartItems, 
  orderType = 'dine_in',
  customerId = null,
  onCustomerChange = null, // Add callback for customer changes
  branchId,
  deliveryProvider = null,
  selectedTable = null, // Add selected table prop
  onTableClear = null, // Add callback to clear selected table
  onOrderCalculationUpdate = null, // Add callback for order calculation updates
  currentCustomer = null,
  setCurrentCustomer,
  webPromotions = [],
  setWebPromotions,
  selectedWebPromo = null,
  setSelectedWebPromo
}) => {
  const [orderCalculation, setOrderCalculation] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [serviceFeeRate, setServiceFeeRate] = useState(10); // Default 10%
  const [packageFeeRate, setPackageFeeRate] = useState(5); // Default 5%

  // Web promotions popup visibility state
  const [showWebPromoPopup, setShowWebPromoPopup] = useState(false);

  // Fetch walking customer by mobile number
  const fetchWalkingCustomer = useCallback(async () => {
    try {
      console.log('🚶 Fetching walking customer by mobile: 0000000000');
      const response = await CustomerService.getCustomerByMobile('0000000000');
      if (response?.success && response.data) {
        // Handle different response structures
        const walkingCustomer = response.data.customer || response.data;
        console.log('✅ Walking customer fetched:', walkingCustomer);
        console.log('🔑 Walking customer ID:', walkingCustomer._id);
        setCurrentCustomer(walkingCustomer);
        // Update parent's customer ID if callback exists
        if (onCustomerChange) {
          console.log('📤 [CurrentOrder] Calling onCustomerChange with walking customer ID:', walkingCustomer._id);
          onCustomerChange(walkingCustomer._id);
          console.log('✅ [CurrentOrder] onCustomerChange callback completed');
        } else {
          console.warn('⚠️ [CurrentOrder] onCustomerChange callback not provided!');
        }
        return walkingCustomer;
      } else {
        console.warn('⚠️ Walking customer not found in database');
        return null;
      }
    } catch (error) {
      console.warn('⚠️ Walking customer with mobile 0000000000 not found in database');
      console.warn('ℹ️ Please create a walking customer with mobile number 0000000000');
      // Don't set any customer - leave it as null until user selects one
      return null;
    }
  }, [onCustomerChange]);

  // Memoize fetchCustomerDetails to avoid dependency issues
  const fetchCustomerDetails = useCallback(async (customerIdToFetch) => {
    try {
      console.log('🌐 API call: Fetching customer with ID:', customerIdToFetch);
      const response = await CustomerService.getCustomerById(customerIdToFetch);
      if (response?.success && response.data) {
        console.log('✅ Customer data fetched:', response.data);
        
        // Handle nested customer data structure
        const customerData = response.data.customer || response.data;
        console.log('🎯 Extracted customer object:', customerData);
        setCurrentCustomer(customerData);
      } else {
        console.warn('⚠️ Customer fetch failed or no data:', response);
        throw new Error('Customer not found');
      }
    } catch (error) {
      console.error('❌ Error fetching customer details:', error);
      // Fetch walking customer as fallback
      console.log('🚶 Fetching walking customer as fallback...');
      await fetchWalkingCustomer();
    }
  }, [fetchWalkingCustomer]);

  // Fetch customer details when customerId changes
  useEffect(() => {
    // If no customerId provided, shouldn't happen anymore since we hardcode it
    if (!customerId) {
      console.log('⚠️ No customerId provided');
      return;
    }
    
    // Only fetch if we don't already have customer data or if the ID has actually changed
    if (!currentCustomer || currentCustomer._id !== customerId) {
      console.log('🔄 Fetching customer details for ID:', customerId);
      fetchCustomerDetails(customerId);
    } else {
      console.log('ℹ️ Customer already loaded, skipping fetch');
    }
  }, [customerId, currentCustomer, fetchCustomerDetails]);

  // Debug: Log when current customer changes
  useEffect(() => {
    console.log('🎯 Current customer state updated:', currentCustomer);
  }, [currentCustomer]);

  // Fetch system configuration values
  useEffect(() => {
    const fetchConfigValues = async () => {
      try {
        console.log('⚙️ Fetching system configuration values...');
        const [serviceFee, packageFee] = await Promise.all([
          systemConfigService.getServiceFeeRate(),
          systemConfigService.getPackageFeeRate()
        ]);
        
        setServiceFeeRate(serviceFee);
        setPackageFeeRate(packageFee);
        
        console.log('✅ Configuration values loaded:', {
          serviceFeeRate: serviceFee,
          packageFeeRate: packageFee
        });
      } catch (error) {
        console.error('❌ Error fetching configuration values:', error);
        // Keep default values on error
      }
    };

    fetchConfigValues();
  }, []); // Only run once on component mount

  const handleCustomerSelect = (customer) => {
    console.log('👤 Customer selected:', customer);
    setCurrentCustomer(customer);
    setShowCustomerModal(false);
    // Reset any previously selected web promo for this new customer
    setSelectedWebPromo(null);
    setWebPromotions([]);

    // Notify parent component of customer change
    if (onCustomerChange) {
      onCustomerChange(customer._id);
      console.log('📞 Notifying parent of customer change to ID:', customer._id);
    }

    // Fetch web promotions for non-walking customers
    const mobile = customer.mobileNumber;
    if (mobile && mobile !== '0000000000') {
      webPromotionService.fetchCustomerWebPromotions(mobile).then(promos => {
        if (promos && promos.length > 0) {
          setWebPromotions(promos);
          setShowWebPromoPopup(true);
        }
      }).catch(() => {});
    }

    console.log('✅ Customer display updated to:', `${customer.name} - ${customer.mobileNumber}`);
  };

  // Calculate order whenever cart changes
  const calculateOrder = useCallback(async () => {
    console.log('🔄 calculateOrder called with customerId:', customerId);
    
    if (!cartItems.length || !branchId) {
      setOrderCalculation(null);
      setCalculationError(null);
      
      // Notify parent component that calculation is cleared
      if (onOrderCalculationUpdate) {
        onOrderCalculationUpdate(null);
      }
      
      // Log when calculation is skipped
      console.log('⏭️ Skipping calculation - no cart items or branch ID');
      return;
    }

    setIsCalculating(true);
    setCalculationError(null);

    try {
      const requestBody = {
        orderItems: cartItems.map(item => {
          // Extract the correct product ID from the cart item
          // item.product._id is the actual product ID that the API expects
          // item.id is the branch-product inventory ID
          const productId = item.product?._id || item.productId || item.id;
          
          if (!productId) {
            console.warn('❌ Cart item missing product ID:', item);
          }
          
          return {
            productId: productId,
            quantity: item.quantity || 1
          };
        }).filter(item => item.productId), // Filter out items without product ID
        orderType: orderType,
        branchId: branchId
      };

      // Validate that we have valid order items
      if (requestBody.orderItems.length === 0) {
        throw new Error('No valid products found in cart');
      }

      // Convert order type to API format
      const getApiOrderType = (orderType) => {
        switch (orderType) {
          case 'dine-in':
            return 'dine_in';
          case 'takeaway':
            return 'take_away';
          case 'delivery':
            return 'delivery';
          default:
            return orderType.replace('-', '_'); // fallback for any other hyphenated types
        }
      };
      
      const apiOrderType = getApiOrderType(orderType);
      requestBody.orderType = apiOrderType;

      // Add customerId if available (use walking customer as default)
      const effectiveCustomerId = customerId || DEFAULT_CUSTOMERS.WALKING_CUSTOMER;
      requestBody.customerId = effectiveCustomerId;

      // Add webPromotion payload if an offer is selected
      if (selectedWebPromo) {
        requestBody.webPromotion = {
          webCustomerPromotionId: selectedWebPromo._id,
          title: selectedWebPromo.promotionSnapshot.title,
          reward: selectedWebPromo.promotionSnapshot.reward,
          discountValue: selectedWebPromo.promotionSnapshot.discountValue,
          freeProductId: selectedWebPromo.promotionSnapshot.freeProductId,
          freeProductName: selectedWebPromo.promotionSnapshot.freeProductName,
          minOrderValue: selectedWebPromo.promotionSnapshot.minOrderValue
        };
      }

      console.log('📊 Calculating order with customer ID:', effectiveCustomerId);
      console.log('📝 Calculate request body:', requestBody);

      // Add delivery provider if order type is delivery
      if (orderType === 'delivery' && deliveryProvider) {
        requestBody.deliveryProvider = deliveryProvider;
      }

      const response = await ApiService.post('/orders/calculate', requestBody);
      
      if (response.success && response.data) {
        setOrderCalculation(response.data);
        
        // Notify parent component of calculation update
        if (onOrderCalculationUpdate) {
          onOrderCalculationUpdate(response.data);
        }
        
      } else {
        // Handle case where calculation completed with issues
        if (response.message && response.message.includes('completed with issues')) {
          // Still use the data if available, but show a warning
          if (response.data) {
            setOrderCalculation(response.data);
          } else {
            throw new Error('Order calculation completed with issues but no data returned');
          }
        } else {
          throw new Error(response.message || 'Failed to calculate order');
        }
      }
    } catch (error) {
      console.error('❌ Order calculation failed:', error);
      setCalculationError(error.message || 'Failed to calculate order');
    } finally {
      setIsCalculating(false);
    }
  }, [cartItems, orderType, customerId, branchId, deliveryProvider, selectedWebPromo, onOrderCalculationUpdate]);

  // Debug: Track customerId changes
  useEffect(() => {
    console.log('🔍 CustomerId changed from props:', customerId);
    console.log('🔍 Current customer state:', currentCustomer);
    console.log('🔍 Cart items length:', cartItems.length);
  }, [customerId, currentCustomer, cartItems.length]);

  // Trigger calculation when dependencies change
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      calculateOrder();
    }, 300); // Debounce for 300ms

    return () => clearTimeout(debounceTimer);
  }, [calculateOrder]);

  // Debug: Expose cart items to window for testing
  useEffect(() => {
    window.currentCartItems = cartItems;
    window.currentOrderCalculation = orderCalculation;
  }, [cartItems, orderCalculation]);

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeItem(itemId);
      return;
    }

    // Find the item to check stock
    const item = cartItems.find(item => 
      item.id === itemId || item.productId === itemId || item._id === itemId
    );
    
    if (item && item.currentStock && newQuantity > item.currentStock) {
      console.warn('Cannot update quantity - insufficient stock:', item.product?.name, 'Available:', item.currentStock, 'Requested:', newQuantity);
      return;
    }

    setCartItems(prevItems =>
      prevItems.map(item =>
        (item.id === itemId || item.productId === itemId || item._id === itemId)
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const removeItem = (itemId) => {
    setCartItems(prevItems =>
      prevItems.filter(item => 
        item.id !== itemId && item.productId !== itemId && item._id !== itemId
      )
    );
  };

  const getProductPrice = (item) => {
    const product = item.product || item;
    
    // If we have order calculation data, use the calculated price
    if (orderCalculation) {
      const productResult = orderCalculation.productPromotions?.productResults?.find(
        result => result.productId === (item.product?._id || item.id || item.productId || item._id)
      );
      
      if (productResult?.promotionResult?.unitDetails?.finalPricePerUnit) {
        return productResult.promotionResult.unitDetails.finalPricePerUnit;
      }
    }
    
    // Fallback to product price based on order type
    switch (orderType) {
      case 'delivery':
        return product.uberPrice || product.pickMePrice || product.outletPrice || 0;
      case 'takeaway':
        return product.outletPrice || 0;
      default: // dine_in
        return product.outletPrice || 0;
    }
  };

  const getOriginalProductPrice = (item) => {
    const product = item.product || item;
    
    // Always return the original product price based on order type, ignoring promotions
    switch (orderType) {
      case 'delivery':
        return product.uberPrice || product.pickMePrice || product.outletPrice || 0;
      case 'takeaway':
        return product.outletPrice || 0;
      default: // dine_in
        return product.outletPrice || 0;
    }
  };

  const getProductDiscount = (item) => {
    if (!orderCalculation) return 0;
    
    // Find the product result for this item
    const productResult = orderCalculation.productPromotions?.productResults?.find(
      result => result.productId === (item.product?._id || item.id || item.productId || item._id)
    );
    
    return productResult?.promotionResult?.unitDetails?.discountPerUnit || 0;
  };

  const getOrderTotals = () => {
    // Return zeros if no API data available
    if (!orderCalculation) {
      return {
        subtotal: 0,
        discount: 0,
        serviceFee: 0,
        packageFee: 0,
        total: 0,
        feeType: 'service'
      };
    }

    // Extract values from the API response structure
    const productPromotions = orderCalculation.productPromotions;
    const orderPromotions = orderCalculation.orderPromotions;
    
    // Get subtotal (original price before any discounts)
    const subtotal = productPromotions?.summary?.overallTotals?.originalPrice || 0;
    
    // Get product-level discounts
    const productDiscount = productPromotions?.summary?.overallTotals?.totalDiscount || 0;
    
    // Get order-level discounts 
    const orderDiscount = orderPromotions?.orderDetails?.totalDiscount || 0;
    
    // Total discount is sum of product and order discounts
    const totalDiscount = productDiscount + orderDiscount;
    
    // Calculate web promotion discount if selected
    let webPromoDiscount = 0;
    if (selectedWebPromo) {
      const snap = selectedWebPromo.promotionSnapshot;
      if (snap.reward === 'fixed_discount') {
        webPromoDiscount = snap.discountValue || 0;
      } else if (snap.reward === 'percentage_discount') {
        webPromoDiscount = (subtotal * (snap.discountValue || 0)) / 100;
      } else if (snap.reward === 'free_item') {
        const matchedCartItem = cartItems.find(
          (item) => String(item.product?._id || item.productId || item.id) === String(snap.freeProductId)
        );
        if (matchedCartItem) {
          webPromoDiscount = getOriginalProductPrice(matchedCartItem);
        }
      }
    }

    // Display generic discount after deducting the web promotion discount (if any) to prevent double counting
    const displayGeneralDiscount = Math.max(0, totalDiscount - webPromoDiscount);

    // Price after discounts from API
    const priceAfterDiscount = Math.max(0, orderPromotions?.orderDetails?.finalOrderValue ?? (subtotal - totalDiscount));
    
    // Determine fee type and calculate based on order type
    let serviceFee = 0;
    let packageFee = 0;
    let feeType = 'service';
    
    // Convert order type to match the API format for comparison
    const normalizedOrderType = orderType?.replace('_', '-').toLowerCase();
    
    if (normalizedOrderType === 'delivery') {
      // For delivery orders, there are no fees
      packageFee = 0;
      serviceFee = 0;
      feeType = 'none';
      console.log(`📦 Delivery order: no package fee or service fee applied`);
    } else if (normalizedOrderType === 'dine-in') {
      // For dine-in orders only, use service fee
      serviceFee = priceAfterDiscount * (serviceFeeRate / 100);
      feeType = 'service';
      console.log(`🍽️ Dine-in order: applying ${serviceFeeRate}% service fee = Rs.${serviceFee.toFixed(2)}`);
    } else if (normalizedOrderType === 'takeaway') {
      // For takeaway orders, use package fee
      packageFee = priceAfterDiscount * (packageFeeRate / 100);
      feeType = 'package';
      console.log(`🥡 Takeaway order: applying ${packageFeeRate}% packaging fee = Rs.${packageFee.toFixed(2)}`);
    } else {
      // Fallback for any other order type - no fees
      console.log(`❓ Unknown order type: ${normalizedOrderType} - no fees applied`);
    }

    // Final total = (subtotal - discount) + applicable fee
    const total = priceAfterDiscount + serviceFee + packageFee;
    
    return {
      subtotal,
      discount: displayGeneralDiscount,
      webPromoDiscount,
      serviceFee,
      packageFee,
      total,
      feeType,
      feeRate: feeType === 'service' ? serviceFeeRate : (feeType === 'package' ? packageFeeRate : 0)
    };
  };

  const totals = getOrderTotals();

  return (
    <div className="w-[28rem] bg-white border-l border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            🛒 Current Order
          </h2>
          {isCalculating && (
            <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
          )}
        </div>
        
        {/* Selected Table Display - Only for dine-in orders */}
        {selectedTable && orderType === 'dine-in' && (
          <div className="mt-3 p-2 bg-orange-50 rounded-lg border border-orange-200">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2">
                <span className="font-medium text-orange-800">Table {selectedTable.number}</span>
                <span className="text-orange-600">• {selectedTable.capacity} seats</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                  {selectedTable.availability === 'available' ? 'Available' : 'Reserved'}
                </span>
                {onTableClear && (
                  <button
                    onClick={() => onTableClear()}
                    className="text-orange-600 hover:text-orange-800 hover:bg-orange-100 p-1 rounded transition-colors"
                    title="Clear table selection"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Customer Info */}
      <div 
        className="p-4 border-b border-gray-100 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => setShowCustomerModal(true)}
        title="Click to select or change customer"
      >
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <User className="h-4 w-4" />
          <span className="flex-1">
            {currentCustomer 
              ? `${currentCustomer.name} - ${currentCustomer.mobileNumber}`
              : 'Walking Customer - 0000000000'
            }
          </span>
          <div className="text-orange-500 hover:text-orange-600 transition-colors">
            <Plus className="h-4 w-4" />
          </div>
        </div>
      </div>

      {/* Cart Items */}
      <div className="flex-1 overflow-y-auto">
        {cartItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 p-8">
            <div className="text-4xl mb-4">🛒</div>
            <p className="text-center">No items in cart</p>
            <p className="text-sm text-center mt-2">Add products to get started</p>
          </div>
        ) : (
          <div className="p-4">
            {/* Table Header */}
            <div className="grid grid-cols-4 gap-4 pb-3 border-b border-gray-200 text-sm font-medium text-gray-600">
              <div>Item</div>
              <div className="text-center">Qty</div>
              <div className="text-center">Discount</div>
              <div className="text-right">Subtotal</div>
            </div>
            
            {/* Table Items */}
            <div className="space-y-4 mt-4">
              {cartItems.map((item, index) => {
                const price = getProductPrice(item);
                const originalPrice = getOriginalProductPrice(item);
                const discount = getProductDiscount(item)*item.quantity;
                // Use a more consistent key that prioritizes product ID over item ID
                const productId = item.productId || item.product?._id || item.id;
                const itemKey = productId ? `product-${productId}` : `item-${index}`;
                const itemId = item.id || item.productId || item._id || `item-${index}`;
                const product = item.product || item;
                const subtotal = price * item.quantity;
                
                console.log('🎨 Rendering cart item:', {
                  index,
                  itemKey,
                  itemId,
                  productId,
                  name: item.name,
                  quantity: item.quantity
                });
                
                // Check if this item has stock issues from API response
                const stockResult = orderCalculation?.stockCheck?.stockCheckResults?.find(
                  result => result.productId === (item.id || item.productId || item._id)
                );
                
                const hasStockIssue = stockResult && !stockResult.isAvailable;
                
                return (
                  <div key={itemKey} className="grid grid-cols-4 gap-4 items-center py-2">
                    {/* Item Name and Price */}
                    <div className="flex flex-col">
                      <h4 className="font-medium text-gray-900 text-sm">
                        {product.name || 'Unknown Product'}
                      </h4>
                      <p className="text-xs text-gray-500">
                        Rs.{originalPrice.toFixed(2)}
                      </p>
                      {hasStockIssue && (
                        <p className="text-xs text-red-600 font-medium">
                          ⚠️ Out of Stock
                        </p>
                      )}
                    </div>
                    
                    {/* Quantity Controls */}
                    <div className="flex items-center justify-center space-x-1">
                      <button
                        onClick={() => updateQuantity(itemId, item.quantity - 1)}
                        className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 hover:bg-gray-50 text-sm"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="font-medium w-8 text-center text-sm">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(itemId, item.quantity + 1)}
                        className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 hover:bg-gray-50 text-sm"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    
                    {/* Discount */}
                    <div className="text-center text-sm">
                      {discount > 0 ? `${discount.toFixed(2)}` : '0.00'}
                    </div>
                    
                    {/* Subtotal with Delete */}
                    <div className="flex items-center justify-end space-x-2">
                      <span className="font-medium text-sm">
                        {subtotal.toFixed(2)}
                      </span>
                      <button
                        onClick={() => removeItem(itemId)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {calculationError && (
        <div className="p-4 bg-red-50 border-t border-red-200">
          <div className="flex items-center space-x-2 text-red-600 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{calculationError}</span>
          </div>
        </div>
      )}

      {/* Order Summary */}
      {cartItems.length > 0 && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="space-y-2 text-sm">

            {/* Applied Web Offer badge — inline, inside the cart panel */}
            {selectedWebPromo && (
              <div style={{
                background: 'linear-gradient(135deg,#f97316,#ea580c)',
                borderRadius: 10,
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 6,
                boxShadow: '0 2px 10px rgba(249,115,22,0.35)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Gift size={14} color="white" />
                  <div>
                    <p style={{ color: 'white', fontWeight: 700, fontSize: 11, margin: 0 }}>🎁 {selectedWebPromo.promotionSnapshot.title}</p>
                    <p style={{ color: 'rgba(255,255,255,0.82)', fontSize: 10, margin: 0 }}>{selectedWebPromo.promotionSnapshot.rewardLabel}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedWebPromo(null)}
                  style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, padding: '3px 6px', cursor: 'pointer', color: 'white' }}
                  title="Remove offer"
                >
                  <X size={12} />
                </button>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-medium">Rs.{totals.subtotal.toFixed(2)}</span>
            </div>
            {totals.discount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Discount</span>
                <span>-Rs.{totals.discount.toFixed(2)}</span>
              </div>
            )}
            {totals.webPromoDiscount > 0 && (
              <div className="flex justify-between text-orange-600">
                <span className="flex items-center gap-1"><Gift size={12} /> Web Offer</span>
                <span>-Rs.{totals.webPromoDiscount.toFixed(2)}</span>
              </div>
            )}
            {totals.feeType === 'service' && totals.serviceFee > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Service fee({totals.feeRate}%)</span>
                <span className="font-medium">Rs.{totals.serviceFee.toFixed(2)}</span>
              </div>
            )}
            {totals.feeType === 'package' && totals.packageFee > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Package fee({totals.feeRate}%)</span>
                <span className="font-medium">Rs.{totals.packageFee.toFixed(2)}</span>
              </div>
            )}
            <div className="border-t border-gray-300 pt-2 flex justify-between text-lg font-bold">
              <span>Total</span>
              <span>Rs.{totals.total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Stock Alerts */}
      {orderCalculation?.stockCheck && !orderCalculation.stockCheck.summary.overallOrderFulfillable && (
        <div className="p-4 bg-red-50 border-t border-red-200">
          <div className="flex items-center space-x-2 text-red-800 text-sm">
            <AlertCircle className="h-4 w-4" />
            <div>
              <p className="font-medium">Stock Issues Detected</p>
              <p className="text-xs">
                {orderCalculation.stockCheck.summary.unavailableProducts} of {orderCalculation.stockCheck.summary.totalProductsRequested} products unavailable
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stock Warnings for Low Stock */}
      {orderCalculation?.stockCheck?.stockCheckResults?.some(result => 
        result.isAvailable && result.availableStock <= result.requestedQuantity + 5
      ) && (
        <div className="p-4 bg-yellow-50 border-t border-yellow-200">
          <div className="flex items-center space-x-2 text-yellow-800 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>Some items have low stock remaining</span>
          </div>
        </div>
      )}

      {/* Applied Promotions */}
      {orderCalculation?.orderPromotions?.hasPromotion && (
        <div className="p-4 bg-green-50 border-t border-green-200">
          <div className="text-green-800 text-sm">
            <p className="font-medium">🎉 Promotion Applied!</p>
            <p className="text-xs">{orderCalculation.orderPromotions.promotion.name}</p>
            <p className="text-xs">
              Save Rs.{orderCalculation.orderPromotions.orderDetails.totalSavings.toFixed(2)}
            </p>
          </div>
        </div>
      )}

      {/* Customer Selection Modal */}
      <CustomerSelectionModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onCustomerSelect={handleCustomerSelect}
        currentCustomerId={customerId}
        branchId={branchId}
      />

      {/* Web Promotions Popup */}
      {showWebPromoPopup && webPromotions.length > 0 && (
        <WebPromotionsPopup
          promotions={webPromotions}
          customerMobile={currentCustomer?.mobileNumber}
          subtotal={totals.subtotal}
          onClaim={(promo, discountInfo) => {
            setSelectedWebPromo(promo);
            setShowWebPromoPopup(false);
          }}
          onClose={() => setShowWebPromoPopup(false)}
        />
      )}

      {/* Web offer badge is now rendered inline inside the Order Summary section above */}
    </div>
  );
};

export default CurrentOrder;
